// src/components/settings/Settings.js
import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../contexts/AppContext';
import { Card, FormField, Input, Button, Badge, Toggle } from '../layout/UI';
import { useResponsive } from '../../hooks/useResponsive';
import TraccarSettings from '../gps/TraccarSettings';
import { fetchTraccarDevices } from '../../services/traccar';
import { syncOneTruck } from '../../services/syncTravelHistory';

export default function Settings() {
  const { settings, updateSettings, gpsDevices, addGPSDevice, updateGPSDevice, showNotification, trucks, travelHistory, bulkAddTravelHistory } = useApp();
  const { isMobile } = useResponsive();
  const [form, setForm] = useState({ ...settings });
  const [saved, setSaved] = useState(false);
  const [newDocType, setNewDocType] = useState('');

  const documentTypes = settings.documentTypes || [];

  const handleAddDocType = () => {
    const trimmed = newDocType.trim();
    if (!trimmed) return;
    if (documentTypes.some(t => t.toLowerCase() === trimmed.toLowerCase())) {
      showNotification('That document type already exists', 'error');
      return;
    }
    updateSettings({ documentTypes: [...documentTypes, trimmed] });
    setNewDocType('');
  };

  const handleRemoveDocType = (type) => {
    if (documentTypes.length <= 1) {
      showNotification('You need at least one document type', 'error');
      return;
    }
    updateSettings({ documentTypes: documentTypes.filter(t => t !== type) });
  };

  // GPS server connection state (moved here from GPS Devices page)
  const [traccarConnected, setTraccarConnected] = useState(false);
  const [traccarDevices, setTraccarDevices] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState(false);

  // Travel History sync
  const [historySyncing, setHistorySyncing] = useState(false);
  const [historySyncProgress, setHistorySyncProgress] = useState(null); // { current, total, truckName }
  const [historySyncResult, setHistorySyncResult] = useState(null); // { imported, trucksProcessed, errors }

  const loadTraccarDevices = useCallback(async () => {
    try {
      const devs = await fetchTraccarDevices();
      setTraccarDevices(devs);
      return devs;
    } catch (e) {
      showNotification('Could not fetch devices: ' + e.message, 'error');
      return [];
    }
  }, [showNotification]);

  async function handleSync() {
    setSyncing(true);
    const devs = await loadTraccarDevices();
    showNotification(`Synced — ${devs.length} device(s) found on GPS server`);
    setSyncing(false);
  }

  async function handleImportAll() {
    setImporting(true);
    const devs = await loadTraccarDevices();
    let imported = 0;
    for (const td of devs) {
      const alreadyLinked = gpsDevices.find(
        g => g.traccarDeviceId === td.id || g.imei === td.uniqueId
      );
      if (!alreadyLinked) {
        addGPSDevice({
          deviceId: td.name || `Device-${td.id}`,
          model: 'Other',
          simCard: '',
          firmware: '',
          imei: td.uniqueId || '',
          traccarDeviceId: td.id,
          status: 'available',
          lastPing: td.lastUpdate ? new Date(td.lastUpdate).toLocaleString('en-PH') : '—',
        });
        imported++;
      }
    }
    showNotification(imported > 0 ? `✅ ${imported} device(s) imported` : 'All devices already imported');
    setImporting(false);
  }

  const availableForImport = traccarDevices.filter(
    td => !gpsDevices.find(g => g.traccarDeviceId === td.id || g.imei === td.uniqueId)
  );

  // Detects devices linked by IMEI (same physical hardware) but whose
  // STORED traccarDeviceId no longer matches — this happens if a device
  // was ever removed and re-added on the GPS server, which assigns it a
  // brand new internal ID. The stale stored ID silently breaks
  // connectivity/battery lookups on the GPS Devices page, Live Tracking,
  // and Sync Travel History — all of which read the stored ID directly,
  // unlike this panel's own display, which already has an IMEI fallback
  // just for showing the "linked" badge correctly.
  const staleLinks = traccarDevices
    .map(td => {
      const linked = gpsDevices.find(g => g.imei === td.uniqueId);
      return linked && linked.traccarDeviceId !== td.id ? { device: linked, correctId: td.id, traccarName: td.name } : null;
    })
    .filter(Boolean);

  const handleFixStaleLink = async (device, correctId) => {
    await updateGPSDevice(device.id, { traccarDeviceId: correctId });
  };

  const handleFixAllStaleLinks = async () => {
    for (const { device, correctId } of staleLinks) {
      await updateGPSDevice(device.id, { traccarDeviceId: correctId });
    }
    showNotification(`Fixed ${staleLinks.length} outdated device link${staleLinks.length !== 1 ? 's' : ''}`);
  };

  // ── Sync Travel History ──────────────────────────────────────────────────
  // For each truck with a linked GPS device that's registered on the GPS
  // server, pulls its historical positions since the last recorded entry
  // (or the last 30 days, if it has no history yet), reconstructs
  // idle/moving transitions the same way the live app does, resolves each
  // one's location name, and bulk-saves them — a manual catch-up for
  // whatever the live tracker missed while nobody had it open.

  async function handleSyncTravelHistory() {
    const syncableTrucks = trucks.filter(t => {
      const gps = gpsDevices.find(g => g.id === t.gpsDeviceId);
      return gps?.traccarDeviceId;
    });

    if (syncableTrucks.length === 0) {
      showNotification('No trucks have a GPS device registered on the server yet', 'error');
      return;
    }

    setHistorySyncing(true);
    setHistorySyncResult(null);
    let totalImported = 0;
    let trucksWithNewData = 0;
    const errors = [];

    for (let i = 0; i < syncableTrucks.length; i++) {
      const truck = syncableTrucks[i];

      try {
        const gps = gpsDevices.find(g => g.id === truck.gpsDeviceId);
        const existingForTruck = travelHistory.filter(h => h.truckId === truck.id);

        const transitions = await syncOneTruck(truck, gps.traccarDeviceId, existingForTruck, (phase, extra) => {
          if (phase === 'warning') { errors.push(`${truck.plateNumber}: ${extra.message}`); return; }
          setHistorySyncProgress({
            current: i + 1, total: syncableTrucks.length, truckName: truck.plateNumber, phase,
            ...(phase === 'geocoding' ? { geocodeCurrent: extra.current, geocodeTotal: extra.total } : {}),
            ...(phase === 'positions' ? { windowCurrent: extra.current, windowTotal: extra.total } : {}),
          });
        });

        if (transitions.length > 0) {
          try {
            const { insertedCount, skippedCount } = await bulkAddTravelHistory(transitions);
            totalImported += insertedCount;
            if (insertedCount > 0) trucksWithNewData++;
            if (skippedCount > 0) errors.push(`${truck.plateNumber}: ${skippedCount} entr${skippedCount === 1 ? 'y' : 'ies'} couldn't be saved and were skipped (the rest saved fine)`);
          } catch (saveErr) {
            totalImported += saveErr.insertedCount || 0;
            if ((saveErr.insertedCount || 0) > 0) trucksWithNewData++;
            errors.push(`${truck.plateNumber}: ${saveErr.message}${saveErr.insertedCount ? ` (saved ${saveErr.insertedCount} of ${transitions.length} first)` : ''}`);
          }
        }
      } catch (err) {
        errors.push(`${truck.plateNumber}: ${err.message}`);
      }
    }

    setHistorySyncing(false);
    setHistorySyncProgress(null);
    setHistorySyncResult({ imported: totalImported, trucksProcessed: syncableTrucks.length, trucksWithNewData, errors });

    if (errors.length === 0) {
      showNotification(totalImported > 0 ? `✅ Synced ${totalImported} new entries` : 'Already up to date — nothing new to sync');
    } else {
      showNotification(`Synced with ${errors.length} error(s) — see details below`, 'error');
    }
  }

  useEffect(() => { setForm({ ...settings }); }, [settings]);

  const handleSave = () => {
    updateSettings(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 900 }}>

      {/* GPS Server Connection Panel */}
      <TraccarSettings onConnected={connected => {
        setTraccarConnected(connected);
        if (connected) loadTraccarDevices();
      }} />

      {/* GPS Server Devices Panel */}
      {traccarConnected && (
        <Card
          title={`GPS Server Devices ${traccarDevices.length > 0 ? `(${traccarDevices.length})` : ''}`}
          action={
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="sm" variant="ghost" onClick={handleSync} disabled={syncing}>
                {syncing ? '⏳ Syncing...' : '↻ Sync'}
              </Button>
              {staleLinks.length > 0 && (
                <Button size="sm" variant="danger" onClick={handleFixAllStaleLinks}>
                  ⚠ Fix {staleLinks.length} Outdated Link{staleLinks.length !== 1 ? 's' : ''}
                </Button>
              )}
              {availableForImport.length > 0 && (
                <Button size="sm" onClick={handleImportAll} disabled={importing}>
                  {importing ? '⏳ Importing...' : `⬇ Import All (${availableForImport.length})`}
                </Button>
              )}
            </div>
          }
        >
          {staleLinks.length > 0 && (
            <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 9, marginBottom: 14, fontSize: 12, color: 'var(--textSecondary)' }}>
              ⚠ {staleLinks.length} device{staleLinks.length !== 1 ? 's have' : ' has'} an outdated server link — the same physical device (matched by IMEI) now has a different ID on your GPS server, probably from being removed and re-added at some point. This breaks connectivity/battery status and live tracking for it until fixed.
            </div>
          )}
          {traccarDevices.length === 0 ? (
            <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--textFaint)', fontSize: 13 }}>
              No devices found on the GPS server yet.
              <div style={{ fontSize: 11, marginTop: 6, color: '#2a3a5a' }}>
                Add a device in GPS Devices — it will auto-register on the GPS server when you enter an IMEI.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {traccarDevices.map(td => {
                const linked = gpsDevices.find(
                  g => g.traccarDeviceId === td.id || g.imei === td.uniqueId
                );
                const isStale = linked && linked.traccarDeviceId !== td.id;
                const isOnline = td.status === 'online';
                const lastUpdate = td.lastUpdate ? new Date(td.lastUpdate).toLocaleString('en-PH') : '—';

                return (
                  <div key={td.id} style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: 12, padding: '11px 14px', background: 'var(--bgInput)', borderRadius: 9, border: `1px solid ${isStale ? 'rgba(239,68,68,0.35)' : isOnline ? 'rgba(16,185,129,0.2)' : 'var(--bgTag)'}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                      {/* Status dot */}
                      <div style={{ width: 9, height: 9, borderRadius: '50%', background: isOnline ? '#10b981' : '#6b7280', boxShadow: isOnline ? '0 0 8px #10b981' : 'none', flexShrink: 0 }} />

                      {/* Device info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--textPrimary)', fontFamily: 'Syne, sans-serif' }}>{td.name}</span>
                          <Badge color={isOnline ? '#10b981' : '#6b7280'}>{td.status || 'unknown'}</Badge>
                          {isStale
                            ? <Badge color="#ef4444">⚠ Outdated Link ({linked.deviceId})</Badge>
                            : linked
                              ? <Badge color="#3b82f6">✓ {linked.deviceId}</Badge>
                              : <Badge color="#f59e0b">Not imported</Badge>
                          }
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--textFaint)', marginTop: 3, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                          <span>IMEI: <span style={{ fontFamily: 'monospace', color: 'var(--textMuted)' }}>{td.uniqueId}</span></span>
                          <span>GPS ID: <span style={{ color: 'var(--textMuted)' }}>{td.id}</span>{isStale && <span style={{ color: '#f87171' }}> (stored: {linked.traccarDeviceId})</span>}</span>
                          <span>Last update: <span style={{ color: 'var(--textMuted)' }}>{lastUpdate}</span></span>
                        </div>
                      </div>
                    </div>

                    {/* Fix stale link button */}
                    {isStale && (
                      <Button
                        size="sm"
                        variant="danger"
                        style={isMobile ? { width: '100%' } : undefined}
                        onClick={() => handleFixStaleLink(linked, td.id)}
                      >
                        ⚠ Fix Link
                      </Button>
                    )}

                    {/* Import single device button */}
                    {!linked && (
                      <Button
                        size="sm"
                        variant="ghost"
                        style={isMobile ? { width: '100%' } : undefined}
                        onClick={() => {
                          addGPSDevice({
                            deviceId: td.name || `Device-${td.id}`,
                            model: 'Other',
                            simCard: '',
                            firmware: '',
                            imei: td.uniqueId || '',
                            traccarDeviceId: td.id,
                            status: 'available',
                            lastPing: td.lastUpdate ? new Date(td.lastUpdate).toLocaleString('en-PH') : '—',
                          });
                          showNotification(`Imported "${td.name}" into ShipDoTrack`);
                        }}
                      >
                        ⬇ Import
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* Sync Travel History */}
      <Card
        title="🔄 Sync Travel History"
        action={
          <Button onClick={handleSyncTravelHistory} disabled={historySyncing || !traccarConnected} size="sm">
            {historySyncing ? '⏳ Syncing...' : '↻ Sync Now'}
          </Button>
        }
      >
        {!traccarConnected ? (
          <div style={{ fontSize: 12, color: 'var(--textFaint)' }}>
            Connect your GPS server above first — Travel History syncs directly from Traccar's own recorded positions.
          </div>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--textSecondary)', lineHeight: 1.6, marginBottom: historySyncProgress || historySyncResult ? 14 : 0 }}>
            Pulls each truck's real GPS history from your GPS server, reconstructs idle/moving events, and resolves each one's location name — in one click, for every truck at once. Safe to click repeatedly: it only imports what's new since the last sync.
          </div>
        )}

        {historySyncProgress && (
          <div style={{ marginTop: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: 'var(--textSecondary)' }}>
                {historySyncProgress.phase === 'geocoding'
                  ? `📍 Resolving location names for ${historySyncProgress.truckName}...`
                  : `Fetching positions for ${historySyncProgress.truckName}${historySyncProgress.windowTotal > 1 ? ` (day ${Math.min(historySyncProgress.windowCurrent + 1, historySyncProgress.windowTotal)} of ${historySyncProgress.windowTotal})` : ''}...`}
              </span>
              <span style={{ fontSize: 11, color: 'var(--textFaint)' }}>
                {historySyncProgress.phase === 'geocoding'
                  ? `${historySyncProgress.geocodeCurrent} / ${historySyncProgress.geocodeTotal}`
                  : `truck ${historySyncProgress.current} / ${historySyncProgress.total}`}
              </span>
            </div>
            <div style={{ height: 6, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                width: historySyncProgress.phase === 'geocoding'
                  ? `${(historySyncProgress.geocodeCurrent / historySyncProgress.geocodeTotal) * 100}%`
                  : `${(historySyncProgress.current / historySyncProgress.total) * 100}%`,
                height: '100%', background: historySyncProgress.phase === 'geocoding' ? '#3b82f6' : '#f97316', borderRadius: 4, transition: 'width 0.3s ease',
              }} />
            </div>
            {historySyncProgress.phase === 'geocoding' && (
              <div style={{ fontSize: 10, color: 'var(--textFaint)', marginTop: 4 }}>This step is rate-limited to be polite to the free geocoding service — about 1 location per second.</div>
            )}
          </div>
        )}

        {historySyncResult && !historySyncProgress && (
          <div style={{ padding: '12px 14px', background: historySyncResult.errors.length ? 'rgba(239,68,68,0.06)' : 'rgba(16,185,129,0.06)', border: `1px solid ${historySyncResult.errors.length ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)'}`, borderRadius: 9 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--textPrimary)', marginBottom: 4 }}>
              {historySyncResult.imported > 0
                ? `✅ Imported ${historySyncResult.imported} new entries`
                : '✓ Already up to date'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--textMuted)' }}>
              Checked {historySyncResult.trucksProcessed} truck{historySyncResult.trucksProcessed !== 1 ? 's' : ''} · {historySyncResult.trucksWithNewData} had new data
            </div>
            {historySyncResult.errors.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 11, color: '#f87171' }}>
                {historySyncResult.errors.map((e, i) => <div key={i}>⚠ {e}</div>)}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Idle Notification Settings */}
      <Card title="🔔 Idle Truck Notifications">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 10 : 0, justifyContent: 'space-between', padding: '14px 16px', background: 'var(--bgInput)', borderRadius: 10, border: '1px solid var(--border)' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--textPrimary)' }}>Enable Idle Notifications</div>
              <div style={{ fontSize: 11, color: 'var(--textMuted)', marginTop: 3 }}>Show a popup when a truck becomes idle</div>
            </div>
            <Toggle checked={form.idleNotificationEnabled} onChange={v => set('idleNotificationEnabled', v)} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 16, opacity: form.idleNotificationEnabled ? 1 : 0.4, pointerEvents: form.idleNotificationEnabled ? 'all' : 'none' }}>
            <FormField label="Idle Threshold" hint="Minutes before truck is considered idle">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Input type="number" value={form.idleThresholdMinutes} onChange={v => set('idleThresholdMinutes', Math.max(1, parseInt(v) || 1))} min="1" max="60" />
                <span style={{ fontSize: 12, color: 'var(--textMuted)', whiteSpace: 'nowrap' }}>min</span>
              </div>
            </FormField>

            <FormField label="Repeat Alert Every" hint="Minutes between repeated alerts for the same truck">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Input type="number" value={form.idleNotificationRepeatMinutes} onChange={v => set('idleNotificationRepeatMinutes', Math.max(1, parseInt(v) || 1))} min="1" max="120" />
                <span style={{ fontSize: 12, color: 'var(--textMuted)', whiteSpace: 'nowrap' }}>min</span>
              </div>
            </FormField>

            <FormField label="Popup Duration" hint="Seconds the popup stays visible">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Input type="number" value={form.idlePopupDurationSeconds} onChange={v => set('idlePopupDurationSeconds', Math.max(3, parseInt(v) || 8))} min="3" max="60" />
                <span style={{ fontSize: 12, color: 'var(--textMuted)', whiteSpace: 'nowrap' }}>sec</span>
              </div>
            </FormField>
          </div>

          {/* Preview */}
          {form.idleNotificationEnabled && (
            <div style={{ padding: '14px 16px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Preview</div>
              <div style={{ fontSize: 12, color: 'var(--textSecondary)', lineHeight: 1.6 }}>
                A popup notification will appear when a truck has been idle for <strong style={{ color: 'var(--textPrimary)' }}>{form.idleThresholdMinutes} minute{form.idleThresholdMinutes !== 1 ? 's' : ''}</strong>.<br />
                If the truck remains idle, the alert will repeat every <strong style={{ color: 'var(--textPrimary)' }}>{form.idleNotificationRepeatMinutes} minute{form.idleNotificationRepeatMinutes !== 1 ? 's' : ''}</strong>.<br />
                Each popup will auto-dismiss after <strong style={{ color: 'var(--textPrimary)' }}>{form.idlePopupDurationSeconds} second{form.idlePopupDurationSeconds !== 1 ? 's' : ''}</strong>.
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Document Types */}
      <Card title="📄 Document Types">
        <div style={{ fontSize: 12, color: 'var(--textSecondary)', marginBottom: 14 }}>
          These are the options shown in the "Document Type" dropdown when encoding a new document. Changes apply for every user immediately.
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <Input
              value={newDocType}
              onChange={setNewDocType}
              placeholder="e.g. Customs Clearance"
            />
          </div>
          <Button onClick={handleAddDocType} disabled={!newDocType.trim()}>＋ Add</Button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {documentTypes.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--textFaint)' }}>No document types configured yet.</div>
          )}
          {documentTypes.map(type => (
            <div
              key={type}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'var(--bgInput)', border: '1px solid var(--border)', borderRadius: 20,
                padding: '6px 8px 6px 14px', fontSize: 12, color: 'var(--textSecondary)',
              }}
            >
              {type}
              <button
                onClick={() => handleRemoveDocType(type)}
                title="Remove"
                style={{
                  background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '50%',
                  color: '#f87171', cursor: 'pointer', width: 20, height: 20, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0, padding: 0,
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      </Card>

      {/* General Settings */}
      <Card title="⚙️ General">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <FormField label="Company / System Name">
            <Input value={form.companyName} onChange={v => set('companyName', v)} placeholder="ShipDoTrack" />
          </FormField>
          <FormField label="Timezone">
            <select value={form.timezone} onChange={e => set('timezone', e.target.value)}
              style={{ background: 'var(--bgInput)', border: '1.5px solid var(--borderInput)', borderRadius: 9, padding: '10px 12px', color: 'var(--textPrimary)', fontSize: 13, outline: 'none', width: '100%', fontFamily: 'DM Sans, sans-serif', cursor: 'pointer' }}>
              {['Asia/Manila', 'Asia/Singapore', 'Asia/Hong_Kong', 'Asia/Tokyo', 'UTC'].map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </FormField>
        </div>
      </Card>

      {/* Save */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <Button onClick={handleSave} size="lg">
          {saved ? '✓ Saved!' : 'Save Settings'}
        </Button>
        {saved && <span style={{ fontSize: 13, color: '#34d399' }}>Settings saved successfully</span>}
      </div>
    </div>
  );
}
